// BatiAssistAssistantsPage.jsx
// Page Super Assistants dans le contexte Espace Pro Abracadabati

import { motion } from 'framer-motion';
import { 
  Users, ArrowRight, HeadphonesIcon, Clock, 
  CheckCircle, Star, MessageSquare, Phone
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import EspaceProNav from '../../components/layout/EspaceProNav';
import ImmersiveEspaceProWrapper from '../../components/layout/ImmersiveEspaceProWrapper';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const services = [
  { icon: Phone, title: "Appels entrants", desc: "Ton assistant répond à tes appels et qualifie les prospects" },
  { icon: MessageSquare, title: "Messages & emails", desc: "Traitement des emails et messages clients en ton nom" },
  { icon: CheckCircle, title: "Suivi administratif", desc: "Relances, rappels, mise à jour des dossiers..." },
  { icon: Clock, title: "Gestion planning", desc: "Organisation de tes rendez-vous et optimisation de tes tournées" },
];

const assistants = [
  { name: "Marie", role: "Assistante commerciale", specialty: "Qualification prospects", avatar: "👩‍💼" },
  { name: "Thomas", role: "Assistant administratif", specialty: "Facturation & relances", avatar: "👨‍💼" },
  { name: "Sophie", role: "Assistante planning", specialty: "Gestion RDV", avatar: "👩‍💻" },
];

export default function BatiAssistAssistantsPage() {
  return (
    <ImmersiveEspaceProWrapper>
      <div className="min-h-screen bg-white" data-testid="batiassist-assistants-page">
        <UnifiedHeader />
        <EspaceProNav />
      
      <main>
        {/* Hero */}
        <section className="pt-16 pb-16 md:pt-20 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
                <Users className="w-4 h-4" />
                Super Assistants
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
                Une équipe d'assistants{' '}
                <span className="text-[#F97316]">à ton service</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10">
                Des assistants humains formés aux métiers du bâtiment qui gèrent tes tâches administratives 
                pendant que tu te concentres sur ton cœur de métier.
              </p>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                  Découvrir les Super Assistants
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>

        {/* Assistants Team */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Rencontre ton <span className="text-[#F97316]">équipe</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {assistants.map((assistant, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-6 bg-slate-50 rounded-2xl text-center hover:shadow-lg transition-shadow"
                >
                  <div className="text-6xl mb-4">{assistant.avatar}</div>
                  <h3 className="text-xl font-bold text-slate-800">{assistant.name}</h3>
                  <p className="text-[#1E3A5F] font-medium mb-2">{assistant.role}</p>
                  <p className="text-sm text-slate-500">{assistant.specialty}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Ce qu'ils font <span className="text-[#F97316]">pour toi</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {services.map((service, idx) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex gap-4 p-6 bg-white rounded-2xl shadow-sm"
                  >
                    <div className="w-14 h-14 rounded-xl bg-[#1E3A5F] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-800 mb-2">{service.title}</h3>
                      <p className="text-slate-600">{service.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] rounded-3xl p-8 md:p-12 text-center"
            >
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-[#F97316] fill-[#F97316]" />
                ))}
              </div>
              <p className="text-xl md:text-2xl text-white mb-6 italic">
                "Depuis que j'ai mon assistante BatiAssist, j'ai retrouvé du temps pour moi et ma famille. 
                Elle gère tout l'administratif pendant que je suis sur les chantiers."
              </p>
              <p className="text-white/80 font-medium">
                Jean-Pierre M. — Plombier à Lyon
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-[#F97316] to-[#EA580C]">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Prêt à déléguer ?
              </h2>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-white hover:bg-gray-100 text-[#F97316] font-bold rounded-full px-10 py-6 text-lg">
                  Réserver mon diagnostic gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      </div>
    </ImmersiveEspaceProWrapper>
  );
}
